import * as moment from "moment";

import { LinkObject } from "@teamprevent/common/model";
import { UserRegistrationStatus } from "@teamprevent/common/models/core";
import { Link } from "@teamprevent/common/task/taskActions/models";
import { ServerModelWithDateStrings } from "@teamprevent/common/utils";

import { WorkGroup } from "../workGroup";
import { CustomerListModel, CustomerState } from "../customer";

import { EmployeeRecordFormModel } from "./schema";

import { Location } from "redux-little-router";

export interface EmployeeRecordsDetailDto {
    readonly employeeRecords: EmployeeRecordDetailModel[];
    readonly tooManyRecords: boolean;
}

export interface EmployeeRecordsDetail {
    readonly employeeRecords: EmployeeRecordInRedux[];
    readonly tooManyRecords: boolean;
}

// Currently the EmployeeRecordDetailModel is the same as the EmployeeRecordListModel
export type EmployeeRecordListModel = EmployeeRecordDetailModel;

export interface EmployeeDocument {
    id: string;
    name: string;
    documentType: string;
    dateCreated: moment.Moment | undefined;
    completedBy?: Link;
}

export interface EmployeeRecordDetailModel {
    readonly id: string;
    readonly title?: string;
    readonly firstName?: string;
    readonly lastName?: string;
    readonly previousLastName?: string;
    readonly gender?: string;
    readonly dateOfBirth: moment.Moment | null;
    readonly personalEmailAddress?: string;
    readonly workEmailAddress?: string;
    readonly homePhoneNumber?: string;
    readonly workPhoneNumber?: string;
    readonly mobilePhoneNumber?: string;
    readonly addressLine1?: string;
    readonly addressLine2?: string;
    readonly addressLine3?: string;
    readonly city?: string;
    readonly county?: string;
    readonly postCode?: string;
    readonly jobTitle?: string;
    readonly personalReferenceNumber?: string;
    readonly secondaryReference?: string;
    readonly anticipatedStartDate?: moment.Moment | null;
    readonly criticalNotes: string | null;
    readonly terminationDate: moment.Moment | null;
    readonly active?: boolean;
    readonly customerId: string;
    readonly customerName: string;
    readonly orgStructureOne?: LinkObject;
    readonly orgStructureTwo?: LinkObject;
    readonly orgStructureThree?: LinkObject;
    readonly orgStructureFour?: LinkObject;
    readonly workGroups?: LinkObject[];
    readonly legacyPersonalId?: string;
    readonly registrationStatus?: UserRegistrationStatus;
    readonly registeredEmailAddress?: string;
    readonly closeMonitoring?: boolean;
}

export interface DeleteEmployeeRecordResult {
    success: boolean;
    error: string;
}

/**
 * @deprecated Something should really be either a list or a detail, not both. This is a legacy type we wish to remove. Replace with one of the following:
 * - `EmployeeRecordDetailModel` if you need the detail properties
 * - `EmployeeRecordListModel` if you need just the list properties
 * - `PartialEmployeeRecordListModel` if you want to use the `EmployeeRecordListModel` properties but referenced code is still using `EmployeeRecord` type
 * - `PartialEmployeeRecordDetailModel` if you want to use the `EmployeeRecordDetailModel` properties but referenced code is still using `EmployeeRecord` type
 * - `EmployeeRecordInRedux` if you just want oh case as it comes from redux store
 */
export type EmployeeRecord = EmployeeRecordListModel & EmployeeRecordDetailModel;
type PartialEmployeeRecordDetailModel = Partial<Omit<EmployeeRecordDetailModel, "id" | "customerId">> & { id: string; customerId: string };
type PartialEmployeeRecordDetailModelInRedux = PartialEmployeeRecordDetailModel & { documents?: EmployeeDocument[] };
type EmployeeRecordListModelInRedux = EmployeeRecordListModel & { listUpdated: moment.Moment };
type EmployeeRecordDetailModelInRedux = EmployeeRecordDetailModel & { listUpdated: moment.Moment; detailUpdated: moment.Moment };
export type EmployeeRecordInRedux = PartialEmployeeRecordDetailModelInRedux | EmployeeRecordListModelInRedux | EmployeeRecordDetailModelInRedux;

export interface CreateEmployeeDocumentPayload {
    employeeRecordId: string;
    customerId: string;
    document: EmployeeDocument;
}

export interface LoadEmployeeDocumentsPayload {
    employeeRecordId: string;
    customerId: string;
    documents: EmployeeDocument[];
}

export interface DeleteEmployeeDocumentPayload {
    employeeRecordId: string;
    employeeDocumentId: string;
    customerId: string;
}

export interface SetCloseMonitoringPayload {
    employeeRecordId: string;
    customerId: string;
    closeMonitoring: boolean;
}

export interface EmployeeRecordBaseModel {
    id: string;
    title: string;
    firstName: string;
    lastName: string;
    previousLastName: string | null;
    gender: string | null;
    dateOfBirth: moment.Moment | null;
    personalEmailAddress: string;
    workEmailAddress: string;
    homePhoneNumber: string;
    workPhoneNumber: string;
    mobilePhoneNumber: string;
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    city: string;
    county: string;
    postCode: string;
    jobTitle: string;
    secondaryReference: string;
    criticalNotes: string;
    terminationDate: moment.Moment | null;
    active: boolean;
    customerId: string;
    customerName: string;
    orgStructureOne?: LinkObject;
    orgStructureTwo?: LinkObject;
    orgStructureThree?: LinkObject;
    orgStructureFour?: LinkObject;
    workGroups?: LinkObject[];
}

export interface EditEmployeeRecordFromOhCaseModel {
    id: string;
    title: string;
    firstName: string;
    lastName: string;
    gender: string;
    dateOfBirth: moment.Moment;
    personalEmailAddress: string;
    mobilePhoneNumber: string;
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    city: string;
    county: string;
    postCode: string;
    jobTitle: string;
}

export interface EmployeeRecordTriageModel extends EmployeeRecordBaseModel {
    personalReferenceNumber: string;
    orgStructureOneName?: string;
    orgStructureTwoName?: string;
    orgStructureThreeName?: string;
    orgStructureFourName?: string;
}

export interface EmployeeRecordState {
    readonly employeeRecordsDetail: EmployeeRecordsDetail;
    readonly workGroups: WorkGroup[];
}

export interface EmployeeRecordSearchModel {
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: moment.Moment;
    personalReferenceNumber: string;
}

export interface MergeToNewEmployeeRecordModel {
    workGroups: string[];
    title: string | null;
    firstName: string | null;
    lastName: string | null;
    secondaryReference: string | null;
    gender: string | null;
    emailAddress: string | null;
    jobTitle: string | null;
    dateOfBirth: moment.Moment | null;
    addressLine1: string | null;
    postcode: string | null;
}

export const emptyMergeToNewEmployeeRecordModel = (): MergeToNewEmployeeRecordModel => ({
    workGroups: [],
    title: null,
    firstName: null,
    lastName: null,
    secondaryReference: null,
    gender: null,
    emailAddress: null,
    jobTitle: null,
    dateOfBirth: null,
    addressLine1: null,
    postcode: null
});

export interface MergeEmployeeRecordModel {
    customerId: string;
    fromEmployeeRecordId: string;
    toEmployeeRecordId: string;
}

export type AppState = EmployeeRecordState & { router: Location } & CustomerState;

export const parseEmployeeModel = (model: ServerModelWithDateStrings<EmployeeRecordDetailModel>): EmployeeRecordDetailModel => {
    return {
        ...model,
        dateOfBirth: model.dateOfBirth ? moment(model.dateOfBirth) : null,
        terminationDate: model.terminationDate ? moment(model.terminationDate) : null,
        anticipatedStartDate: model.anticipatedStartDate ? moment(model.anticipatedStartDate) : null,
    };
};

export const parseEmployeeRecordModelToEditModel = (employeeRecord: EmployeeRecordInRedux, customer: CustomerListModel): EmployeeRecordFormModel => {
    const personalReferenceNumber = employeeRecord?.personalReferenceNumber ?? "";
    const title = employeeRecord?.title ?? "";
    const firstName = employeeRecord?.firstName ?? "";
    const lastName = employeeRecord?.lastName ?? "";
    const previousLastName = employeeRecord?.previousLastName ?? "";
    const gender = employeeRecord?.gender ?? "";
    const dateOfBirth = employeeRecord?.dateOfBirth?.toDate() ?? null;
    const personalEmailAddress = employeeRecord?.personalEmailAddress ?? "";
    const workEmailAddress = employeeRecord?.workEmailAddress ?? "";
    const homePhoneNumber = employeeRecord?.homePhoneNumber ?? "";
    const workPhoneNumber = employeeRecord?.workPhoneNumber ?? "";
    const mobilePhoneNumber = employeeRecord?.mobilePhoneNumber ?? "";
    const addressLine1 = employeeRecord?.addressLine1 ?? "";
    const addressLine2 = employeeRecord?.addressLine2 ?? "";
    const addressLine3 = employeeRecord?.addressLine3 ?? "";
    const city = employeeRecord?.city ?? "";
    const county = employeeRecord?.county ?? "";
    const postCode = employeeRecord?.postCode ?? "";
    const jobTitle = employeeRecord?.jobTitle ?? "";
    const secondaryReference = employeeRecord?.secondaryReference ?? "";
    const criticalNotes = employeeRecord?.criticalNotes ?? "";
    const terminationDate = employeeRecord?.terminationDate?.toDate() ?? null;
    const active = employeeRecord?.active ?? false;
    const customerId = employeeRecord?.customerId ?? customer?.id;
    const customerName = employeeRecord?.customerName ?? customer?.name;
    return {
        ...employeeRecord,
        personalReferenceNumber,
        title,
        firstName,
        lastName,
        previousLastName,
        gender,
        dateOfBirth,
        personalEmailAddress,
        workEmailAddress,
        homePhoneNumber,
        workPhoneNumber,
        mobilePhoneNumber,
        addressLine1,
        addressLine2,
        addressLine3,
        city,
        county,
        postCode,
        jobTitle,
        secondaryReference,
        criticalNotes,
        terminationDate,
        active,
        customerId,
        customerName,
        orgStructureOne: employeeRecord?.orgStructureOne as LinkObject,
    };
};
